import React, { useState, useEffect } from "react";
import {
  Button,
  Box,
  Stack,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { styles } from "./UserInputForm.styles";
import TrademarkInputForm from "./TrademarkInputForm";
import DomainInputForm from "./DomainInputForm";
import { FONT_SIZES } from "../../constants/fontSizes";

const DEFAULT_TLDS = [".com", ".org"]; // other very popular TLDs are .net, .io, .ai, etc.

const fieldExplanations = {
  job_type:
    "Select the type of report you want to generate: Trademark search only, Domain availability only, or Full analysis (both trademark and domain).",
};

const UserInputForm = ({
  onSubmit,
  disabled,
  formData,
  setFormData,
  setResults,
  setStatus,
}) => {
  // Initialize form data with default values if not present
  useEffect(() => {
    if (!formData) {
      setFormData({
        job_type: "full",
        tlds: DEFAULT_TLDS,
      });
    } else if (!formData.job_type) {
      setFormData((prev) => ({
        ...prev,
        job_type: "full",
        tlds: prev.tlds || DEFAULT_TLDS,
      }));
    }
  }, []); // Only run once on mount

  const handleClearAllCompanyNames = () => {
    setFormData((prev) => ({
      ...prev,
      candidate_names: [],
    }));
  };

  const handleClearAllDomainNames = () => {
    setFormData((prev) => ({
      ...prev,
      domain_name_candidates: [],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleJobTypeChange = (e) => {
    setResults(null);
    setStatus(null);
    const newValue = e.target.value;
    setFormData((prev) => ({
      ...prev,
      job_type: newValue,
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const trademarkForm = (
    <TrademarkInputForm
      formData={formData}
      setFormData={setFormData}
      disabled={disabled}
      onClearAllNames={handleClearAllCompanyNames}
    />
  );

  const domainForm = (
    <DomainInputForm
      formData={formData}
      setFormData={setFormData}
      disabled={disabled}
      onClearAllNames={handleClearAllDomainNames}
    />
  );

  const renderFormContent = () => {
    switch (formData?.job_type) {
      case "name_ideation":
      case "trademark":
        return trademarkForm;
      case "domain":
        return domainForm;
      default:
        return (
          <Box>
            {trademarkForm}
            {domainForm}
          </Box>
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form} onKeyDown={handleKeyDown}>
      <Stack sx={styles.stack}>
        <Tooltip
          title={fieldExplanations.job_type}
          arrow
          placement="right"
          enterDelay={200}
          leaveDelay={0}
        >
          <FormControl
            component="fieldset"
            sx={{ ...styles.textField, mb: 2 }}
            size="small"
            variant="standard"
          >
            <InputLabel htmlFor="job-type" sx={styles.jobTypeLabel}>
              Report Type
            </InputLabel>
            <Select
              id="job-type"
              name="job_type"
              value={formData?.job_type || "full"}
              label="Report Type"
              onChange={handleJobTypeChange}
              disabled={disabled}
              sx={styles.jobTypeSelect}
            >
              <MenuItem
                value="name_ideation"
                sx={{ fontSize: FONT_SIZES.PARAGRAPH }}
              >
                <Tooltip
                  title="Generate new brand names and a final report scoring names across several criteria to help you choose the best one."
                  arrow
                  placement="right"
                >
                  <span style={{ fontSize: FONT_SIZES.PARAGRAPH }}>
                    Name Ideation
                  </span>
                </Tooltip>
              </MenuItem>
              <MenuItem
                value="trademark"
                sx={{ fontSize: FONT_SIZES.PARAGRAPH }}
              >
                <Tooltip
                  title="Generate a report focusing only on trademark availability and conflicts"
                  arrow
                  placement="right"
                >
                  <span style={{ fontSize: FONT_SIZES.PARAGRAPH }}>
                    Trademark Report
                  </span>
                </Tooltip>
              </MenuItem>
              <MenuItem value="domain" sx={{ fontSize: FONT_SIZES.PARAGRAPH }}>
                <Tooltip
                  title="Generate a report focusing only on domain name availability and pricing"
                  arrow
                  placement="right"
                >
                  <span style={{ fontSize: FONT_SIZES.PARAGRAPH }}>
                    Domain Report
                  </span>
                </Tooltip>
              </MenuItem>
              <MenuItem value="full" sx={{ fontSize: FONT_SIZES.PARAGRAPH }}>
                <Tooltip
                  title="Generate a comprehensive report covering both trademark and domain availability"
                  arrow
                  placement="right"
                >
                  <span style={{ fontSize: FONT_SIZES.PARAGRAPH }}>
                    Full Report
                  </span>
                </Tooltip>
              </MenuItem>
            </Select>
          </FormControl>
        </Tooltip>

        {renderFormContent()}

        <Button
          type="submit"
          variant="contained"
          disabled={disabled}
          sx={styles.submitButton}
        >
          Submit
        </Button>
      </Stack>
    </form>
  );
};

export default UserInputForm;
